import { useFormik } from "formik";
import { Col, Modal, Row } from "react-bootstrap";
import { waitingRoomCategory } from "../../apps/Queue/Constants";
import Select from "react-select";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { getPractitionerList, getPractitionerAuthorizedList, getSingleUser } from "../../api/Individual";
import { failed, success } from "../../common/Toastify";
import { Button } from "../../common/Button";
import moment from "moment";
import { useSelector } from "react-redux";
import { editEncounter, getEncounterRecord, getQueueRecord } from "../../apps/api/Encounter";
import { editQueueItem } from "../../apps/api/WaitingRoom";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { validation, conditionalValidation } from "../../../utils/constants"

const AddToWaitingRoomModal = ({ data, onShow, onHide, handleShow, onSubmitOverride }) => {
  const navigate = useNavigate();
  const capitalizeWords = (str) => {
    return (str) ?  str
      .toLowerCase()
      .replace(/(^|\s)\S/g, (firstLetter) => firstLetter.toUpperCase()) :
      "";
  };
  const waitingRoomCategoryData = waitingRoomCategory;
  const [myUserId, setMyUserId] = useState("");
  const practUserName = useSelector(
    (state) => state?.auth?.user?.name[0]?.text
  );
  const practitionerId = useSelector(
    (state) => state?.auth?.user?.["custom:unique_id"]
  );
  const [isLoading, setIsLoading] = useState(false);
  const submissionInFlight = useRef(false);
  const handleDismiss = () => {
    if (!submissionInFlight.current) onHide();
  };
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [selectedPriorityLevel, setSelectedPriorityLevel] = useState(null);
  const [encounterData, setEncounterData] = useState(null);
  const [selectedCommunicationChannel, setSelectedCommunicationChannel] =
    useState({
      value: "phone",
      label: "Phone",
      data: "phone",
    });

  const waitingRoomCategoryDataOptions = waitingRoomCategoryData.map((option) => ({
    value: option?.reason,
    label: option?.reason,
    data: option,
  }));

  const formatCategoryOptionLabel = (option, { context }) => {
    if (context === "menu") {
      return (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <span>{option?.label}</span>
          <Tooltip title={option?.data?.description} arrow placement="top">
            <InfoOutlinedIcon fontSize="small" style={{ cursor: "pointer", color: "#005da8", marginLeft: "8px" }} />
          </Tooltip>
        </div>
      );
    }
    return option?.label;
  };
  const priorityLevelOptions = [1, 2, 3, 4, 5, 6, 7, 8].map((option) => ({
    value: option,
    label: option,
    data: option,
  }));

  const communicationChannelOptions = ["Phone"].map((option) => ({
    value: option,
    label: option,
    data: option,
  }));

  const [selectedWaitingRoomCategory, setSelectedWaitingRoomCategory] = useState(null);

  const [practitionerList, setPractitionerList] = useState([]);

  const practitionerOptions = practitionerList.map((practitioner) => ({
    value: practitioner.name?.[0]?.text,
    label: capitalizeWords(practitioner.name?.[0]?.text),
    data: practitioner,
  }));

  useEffect(() => {
    getSingleUser({ id: practitionerId, type: "Practitioner" })
      .then((res) => {
        setMyUserId(res?.data?.link?.[0]?.target?.reference?.split("/")?.[1]);
      })
      .catch((res) => {
        failed(
          res?.response?.data?.message ||
            res?.response?.data?.error ||
            res.message
        );
      });
  }, []);

  useEffect(() => {
    if (!data?.encounterId || onSubmitOverride) return;

    getEncounterRecord({ encounterId: data?.encounterId })
      .then((res) => {
        if (res?.status === true) {
          setEncounterData(res?.data);
        }
      })
      .catch((res) =>
        failed(
          res?.response?.data?.message ||
            res?.response?.data?.error ||
            res.message
        )
      );
  }, []);

  const handleWaitingRoomCategoryChange = (selectedOption) => {
    setSelectedWaitingRoomCategory(selectedOption);
    setSelectedPriorityLevel({
      value: selectedOption?.data?.defaultPriority,
      label: selectedOption?.data?.defaultPriority,
      data: selectedOption?.data?.defaultPriority,
    });
    formik.setFieldValue("waitingRoomCategory", selectedOption?.value);
    if (selectedOption?.data) {
      const defaultPriority = selectedOption.data.defaultPriority;
      formik.setFieldValue("priorityLevel", defaultPriority);
    } else {
      formik.setFieldValue("priorityLevel", null);
    }
  };

  const handlePriorityLevelChange = (selectedOption) => {
    setSelectedPriorityLevel(selectedOption);
    formik.setFieldValue("priorityLevel", selectedOption?.value);
  };

  const handlePractitionerChange = (selectedOption) => {
    setSelectedPractitioner(selectedOption);
    formik.setFieldValue("assignedPractitioner", selectedOption?.data);
  };

  const handleCommunicationChannelChange = (selectedOption) => {
    setSelectedCommunicationChannel(selectedOption);
    formik.setFieldValue("communicationChannel", selectedOption.value);
  };

  const formik = useFormik({
    initialValues: {
      waitingRoomCategory: "",
      priorityLevel: null,
      communicationChannel: selectedCommunicationChannel?.value,
      assignedPractitioner: null,
    },
    validationSchema: Yup.object({
      communicationChannel: conditionalValidation(
        Yup.string(),
        {
          validation,
          mandatory: true,
          label: "Communication Channel"
        })
    }),
    onSubmit: async (values) => {
      if (submissionInFlight.current) return;
      if (!values.waitingRoomCategory?.length) {
        failed("Please select category to proceed");
        return;
      }
      submissionInFlight.current = true;

      if (onSubmitOverride) {
        setIsLoading(true);
        return Promise.resolve().then(() =>
          onSubmitOverride({
            waitingRoomCategory: values.waitingRoomCategory,
            priorityLevel: selectedPriorityLevel?.data || values?.priorityLevel,
            assignedPractitioner: values?.assignedPractitioner,
            communicationChannel: values?.communicationChannel,
          })
        )
          .catch((res) =>
            failed(
              res?.response?.data?.message ||
                res?.response?.data?.error ||
                res?.message ||
                "Failed to submit waiting room details"
            )
          )
          .finally(() => {
            submissionInFlight.current = false;
            setIsLoading(false);
          });
      }

      setIsLoading(true);
      const utcString = new Date().toISOString().split(".")[0] + ".00Z";

        let newEncounterPayload = { ...data };
        delete newEncounterPayload?.participant;
        const assignedPractitioner = values?.assignedPractitioner;
        const actorId = myUserId || newEncounterPayload?.practitionerId;
        const updatedIntakeQuestions = {
          ...newEncounterPayload?.intakeQuestions,
          intakeCommunicationChannel: values?.communicationChannel,
          intakePriorityReason: values?.waitingRoomCategory, // This was changed from intakePriorityReason to intakeWaitingRoomCategory (since this key is used in the waiting-room table we need to keep it as intakePriorityReason)
          intakePriorityLevel: selectedPriorityLevel?.data || values?.priorityLevel,
          intakeAssignedPractitioner: assignedPractitioner
            ? {
                individual: {
                  display: assignedPractitioner?.name[0]?.text,
                  reference: `Practitioner/${assignedPractitioner?.id}`,
                  type: "Practitioner",
                },
                period: {
                  start: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
                  end: null,
                },
              }
            : null,
        };

        const updatedParticipant = [
          {
            individual: {
              display: practUserName || newEncounterPayload?.practitionerName,
              reference: `Practitioner/${actorId}`,
              type: "Practitioner",
            },
            period: {
              start: newEncounterPayload?.period?.start,
              end: utcString,
            },
          },
          {
            individual: {
              display: newEncounterPayload?.patient?.display,
              reference: newEncounterPayload?.patient?.reference,
              type: "Patient",
            },
            period: {
              end: null,
              start: utcString,
            },
          },
        ];


        const updatedPeriod = [
          {
            start: utcString,
            end: utcString,
          },
        ];

        const updatedEncounterPayload = {
          ...newEncounterPayload,
          intakeQuestions: updatedIntakeQuestions,
          status: "arrived",
          participant: updatedParticipant,
          requestedPeriod: updatedPeriod,
          encounterClass: "NULL",
          actor: actorId
            ? {
                reference: `Practitioner/${actorId}`,
                display: practUserName || newEncounterPayload?.practitionerName,
                type: "Practitioner",
              }
            : undefined,
        };

        try {
          const encounterId = newEncounterPayload?.encounterId;
          const orgId = newEncounterPayload?.orgId;
          if (encounterId && orgId) {
            const queueRes = await getQueueRecord({
              encounterId,
              userOrganizationId: orgId,
            });
            if (queueRes?.data?.id) {
              const practitionerHistory = assignedPractitioner?.id
                ? [
                    {
                      actor: {
                        display: assignedPractitioner?.name?.[0]?.text,
                        reference: `Practitioner/${assignedPractitioner.id}`,
                        type: "Practitioner",
                      },
                      assignedBy: {
                        actor: {
                          display: practUserName,
                          reference: `Practitioner/${actorId}`,
                          type: "Practitioner",
                        },
                      },
                      updatedAt: Date.now(),
                    },
                  ]
                : [];
              const newPriority = selectedPriorityLevel?.data || values?.priorityLevel;
              const queuePayload = { id: queueRes.data.id, practitionerHistory };
              if (newPriority != null && newPriority !== queueRes.data.priority) {
                queuePayload.priority = newPriority;
                queuePayload.priorityHistory = [
                  ...(queueRes.data.priorityHistory || []),
                  {
                    actor: {
                      display: practUserName,
                      reference: `Practitioner/${actorId}`,
                      type: "Practitioner",
                    },
                    oldPriority: queueRes.data.priority,
                    newPriority,
                    updatedAt: Date.now(),
                  },
                ];
              }
              const queueUpdate = await editQueueItem(queuePayload, orgId);
              if (queueUpdate?.status !== true) {
                throw queueUpdate;
              }
            }
          }

          const resEncounter = await editEncounter(updatedEncounterPayload);
          if (resEncounter?.status !== true) throw resEncounter;
          if (resEncounter.status === true) {
            success("Patient successfully added to Waiting Room");
            //navigate("/app/waiting-room");
            onHide();
            handleShow();
          }
        } catch (res) {
          failed(
            res?.response?.data?.message ||
              res?.response?.data?.error ||
              res?.message || "Could not add the patient to the waiting room."
          );
        } finally {
          submissionInFlight.current = false;
          setIsLoading(false);
        }
    },
  });

  useEffect(() => {
    getPractitionerAuthorizedList(data?.orgId, data?.patientLocation)
      .then((res) => {
        setPractitionerList(res?.result);
      })
      .catch((res) => {
        failed(
          res?.response?.data?.message ||
            res?.response?.data?.error ||
            res.message
        );
      })
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <Modal
      backdropClassName
      backdrop={"static"}
      show={onShow}
      onHide={handleDismiss}
      size="lg"
      aria-labelledby="waiting-room-modal-title"
      centered
      className="custom-dialog"
    >
      <Modal.Header>
        <Modal.Title id="waiting-room-modal-title">
          Add Patient To Waiting Room
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form
          className="common-form border-fields"
          onSubmit={formik.handleSubmit}
        >
          <>
            <Row>
              <Col>
                <span>Category:</span>
                <Select
                  onChange={handleWaitingRoomCategoryChange}
                  isMulti={false}
                  name="waitingRoomCategory"
                  options={waitingRoomCategoryDataOptions}
                  className="search-select"
                  classNamePrefix="select"
                  value={selectedWaitingRoomCategory}
                  formatOptionLabel={formatCategoryOptionLabel}
                  isClearable={false}
                  isLoading={waitingRoomCategoryData?.length > 0 ? false : true}
                />
              </Col>
              <Col>
                <span>Priority Level (Optional):</span>
                <Select
                  onChange={handlePriorityLevelChange}
                  isMulti={false}
                  name="priorityLevel"
                  options={priorityLevelOptions}
                  className="search-select"
                  classNamePrefix="select"
                  value={selectedPriorityLevel}
                  defaultValue={"1"}
                  isClearable={false}
                  isLoading={false}
                />
              </Col>
            </Row>
            <br />
            <br />

            <Row>
              <Col>
                <span>Assign Practitioner (Optional):</span>
                <Select
                  onChange={handlePractitionerChange}
                  isMulti={false}
                  name="assignedPractitioner"
                  options={practitionerOptions}
                  className="search-select"
                  classNamePrefix="select"
                  value={selectedPractitioner}
                  isClearable={false}
                  isLoading={practitionerList?.length > 0 ? false : true}
                />
              </Col>

              <Col>
                <span>Communication Channel:</span>
                <Select
                  onChange={handleCommunicationChannelChange}
                  isMulti={false}
                  name="communicationChannel"
                  options={communicationChannelOptions}
                  className="search-select"
                  classNamePrefix="select"
                  value={selectedCommunicationChannel}
                  isClearable={false}
                  isLoading={
                    communicationChannelOptions?.length > 0 ? false : true
                  }
                />
              </Col>
            </Row>

            <div className="btn-wrap">
              <Button
                onClick={handleDismiss}
                disabled={isLoading}
                variant="secondary"
                title="Cancel"
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={isLoading}>
                Submit
              </Button>
            </div>
          </>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AddToWaitingRoomModal;
